/**
 * Pronto · Global Search palette (Cmd+K / Ctrl+K)
 * ═══════════════════════════════════════════════════════════════════════
 * Cerca su annunci, contatti, agenda in un colpo. Mostrato in overlay,
 * trigger da Cmd+K (Mac) o Ctrl+K (Win/Linux). Debounced 200ms.
 *
 * iter v29 r105 — Aggiunto su richiesta utente per ridurre i 3-4 click
 * tipici per ritrovare un cliente o un annuncio.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  MagnifyingGlass, House, AddressBook, Calendar, X,
} from "@phosphor-icons/react";

export default function GlobalSearchPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ properties: [], contacts: [], agenda: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // ── Apri/chiudi con scorciatoia ──
  useEffect(() => {
    const onKey = (e) => {
      // Cmd+K (Mac) o Ctrl+K (Win/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 60);
    }
    if (!open) {
      setQ("");
      setResults({ properties: [], contacts: [], agenda: [], total: 0 });
      setActiveIdx(0);
    }
  }, [open]);

  // ── Debounced search ──
  useEffect(() => {
    if (!open || q.trim().length < 2) {
      setResults({ properties: [], contacts: [], agenda: [], total: 0 });
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data || { properties: [], contacts: [], agenda: [], total: 0 });
        setActiveIdx(0);
      } catch (_) {
        setResults({ properties: [], contacts: [], agenda: [], total: 0 });
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  // Flattened list per navigazione tastiera
  const flat = [
    ...results.properties.map((p) => ({ kind: "property", item: p, to: `/dashboard/annunci/${p.id}` })),
    ...results.contacts.map((c) => ({ kind: "contact", item: c, to: `/dashboard/contatti?id=${c.id}` })),
    ...results.agenda.map((a) => ({ kind: "agenda", item: a, to: `/dashboard/agenda` })),
  ];

  const go = (entry) => {
    if (!entry) return;
    setOpen(false);
    navigate(entry.to);
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(flat.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(flat[activeIdx]);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#1C1C1A]/60 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
      onClick={() => setOpen(false)}
      data-testid="global-search-overlay"
    >
      <div
        className="w-full max-w-xl bg-white rounded-xl shadow-2xl overflow-hidden border border-[#E5E7E0]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#EDEAE2]">
          <MagnifyingGlass size={20} className="text-[#9A968B] shrink-0" weight="duotone" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Cerca annunci, contatti, agenda…"
            className="flex-1 outline-none text-[15px] text-[#1C1C1A] placeholder:text-[#9A968B] bg-transparent"
            data-testid="global-search-input"
          />
          <kbd className="hidden sm:inline-flex items-center text-[10px] tracking-wider font-semibold text-[#9A968B] bg-[#F5F4EF] border border-[#E5E7E0] rounded px-1.5 py-0.5">ESC</kbd>
          <button
            onClick={() => setOpen(false)}
            className="sm:hidden text-[#9A968B] hover:text-[#1C1C1A]"
            aria-label="Chiudi"
          >
            <X size={18} />
          </button>
        </div>

        {/* Risultati */}
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-6 text-center text-[12px] text-[#9A968B]">Cerco…</div>
          )}
          {!loading && q.trim().length < 2 && (
            <div className="px-4 py-8 text-center text-[12.5px] text-[#9A968B]">
              Inizia a digitare per cercare ovunque nell'app.<br />
              <span className="text-[11px]">Annunci · Contatti · Agenda</span>
            </div>
          )}
          {!loading && q.trim().length >= 2 && flat.length === 0 && (
            <div className="px-4 py-8 text-center text-[12.5px] text-[#9A968B]">
              Nessun risultato per "<strong>{q}</strong>".
            </div>
          )}

          {results.properties.length > 0 && (
            <Section title="Annunci" icon={House}>
              {results.properties.map((p, i) => {
                const idx = i;
                return (
                  <ResultRow
                    key={p.id}
                    active={activeIdx === idx}
                    onClick={() => go(flat[idx])}
                    onMouseEnter={() => setActiveIdx(idx)}
                    primary={p.title || p.address || "Annuncio senza titolo"}
                    secondary={[p.city, p.status].filter(Boolean).join(" · ")}
                  />
                );
              })}
            </Section>
          )}

          {results.contacts.length > 0 && (
            <Section title="Contatti" icon={AddressBook}>
              {results.contacts.map((c, i) => {
                const idx = results.properties.length + i;
                return (
                  <ResultRow
                    key={c.id}
                    active={activeIdx === idx}
                    onClick={() => go(flat[idx])}
                    onMouseEnter={() => setActiveIdx(idx)}
                    primary={c.name || c.phone || c.email || "Contatto"}
                    secondary={[c.phone, c.email].filter(Boolean).join(" · ")}
                  />
                );
              })}
            </Section>
          )}

          {results.agenda.length > 0 && (
            <Section title="Agenda" icon={Calendar}>
              {results.agenda.map((a, i) => {
                const idx = results.properties.length + results.contacts.length + i;
                return (
                  <ResultRow
                    key={a.id}
                    active={activeIdx === idx}
                    onClick={() => go(flat[idx])}
                    onMouseEnter={() => setActiveIdx(idx)}
                    primary={a.title || a.text?.slice(0, 80) || "Nota"}
                    secondary={a.due_at ? new Date(a.due_at).toLocaleDateString("it-IT") : ""}
                  />
                );
              })}
            </Section>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-[#EDEAE2] bg-[#FAFAF7] text-[10.5px] text-[#9A968B] flex items-center justify-between">
          <span>
            <kbd className="font-mono">↑↓</kbd> navigare · <kbd className="font-mono">↵</kbd> aprire
          </span>
          <span>{results.total} risultati</span>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="py-1">
      <div className="px-4 pt-2 pb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[#9A968B]">
        <Icon size={11} weight="fill" /> {title}
      </div>
      {children}
    </div>
  );
}

function ResultRow({ active, onClick, onMouseEnter, primary, secondary }) {
  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={`w-full text-left px-4 py-2.5 transition-colors ${
        active ? "bg-[#F1F4ED]" : "hover:bg-[#FAFAF7]"
      }`}
    >
      <div className="text-[13.5px] text-[#1C1C1A] font-medium truncate">{primary}</div>
      {secondary && (
        <div className="text-[11.5px] text-[#6B6A65] truncate mt-0.5">{secondary}</div>
      )}
    </button>
  );
}
